import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Slider,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import { toast } from "react-toastify";
import {
  getPaymentPlatformMLWorkspace,
  runPaymentPlatformModelPipeline,
} from "../api/companyApi";

const C = {
  ink: "#0b2a4a",
  blue: "#1e6fe8",
  muted: "#64748b",
  line: "#e3ebf3",
  green: "#12a150",
  amber: "#d97706",
  red: "#dc2626",
};
const OPERATORS = [
  ["equals", "Equals"],
  ["not_equals", "Does not equal"],
  ["greater_than", "Greater than"],
  ["greater_or_equal", "Greater than or equal"],
  ["less_than", "Less than"],
  ["less_or_equal", "Less than or equal"],
  ["contains", "Contains"],
  ["not_contains", "Does not contain"],
  ["between", "Between"],
  ["is_blank", "Is blank"],
  ["is_not_blank", "Is not blank"],
];
const emptyRule = (column = "", post = false) => ({
  column,
  operator: "equals",
  value: "",
  value2: "",
  ...(post ? { action: "reject" } : {}),
});
const money = (value) => `$${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const percent = (value) => value == null ? "—" : `${(Number(value) * 100).toFixed(1)}%`;

function Metric({ label, value, detail, color = C.blue }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.6, borderRadius: 2.5, borderColor: C.line, borderTop: `3px solid ${color}` }}>
      <Typography sx={{ color: C.muted, fontSize: 10.5, fontWeight: 850, textTransform: "uppercase", letterSpacing: ".05em" }}>{label}</Typography>
      <Typography sx={{ color: C.ink, fontSize: 24, fontWeight: 950, mt: 0.35 }}>{value}</Typography>
      <Typography sx={{ color: C.muted, fontSize: 11.5 }}>{detail}</Typography>
    </Paper>
  );
}

function RuleRow({ rule, columns, post = false, onChange, onDelete }) {
  const noValue = ["is_blank", "is_not_blank"].includes(rule.operator);
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: post ? "1.4fr 1.2fr 1fr 1fr auto" : "1.5fr 1.2fr 1fr auto" }, gap: 1, alignItems: "center" }}>
      <TextField select size="small" label="Column" value={rule.column} onChange={(event) => onChange({ column: event.target.value })}>
        {columns.map((column) => (
          <MenuItem key={column.column} value={column.column}>
            {column.label} · {column.coverage}% filled
          </MenuItem>
        ))}
      </TextField>
      <TextField select size="small" label="Condition" value={rule.operator} onChange={(event) => onChange({ operator: event.target.value })}>
        {OPERATORS.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
      </TextField>
      {!noValue && (
        <Stack direction="row" spacing={0.75}>
          <TextField size="small" fullWidth label={rule.operator === "between" ? "From" : "Value"} value={rule.value} onChange={(event) => onChange({ value: event.target.value })} />
          {rule.operator === "between" && <TextField size="small" fullWidth label="To" value={rule.value2} onChange={(event) => onChange({ value2: event.target.value })} />}
        </Stack>
      )}
      {noValue && <Box />}
      {post && (
        <TextField select size="small" label="Then" value={rule.action} onChange={(event) => onChange({ action: event.target.value })}>
          <MenuItem value="accept">Accept</MenuItem>
          <MenuItem value="reject">Reject</MenuItem>
        </TextField>
      )}
      <IconButton aria-label="Remove rule" onClick={onDelete}><DeleteOutlineRoundedIcon /></IconButton>
    </Box>
  );
}

export default function AdminMcaModelPipeline({ tenantId }) {
  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [cleaningRules, setCleaningRules] = useState([]);
  const [timeSeriesEnabled, setTimeSeriesEnabled] = useState(false);
  const [timeSeriesColumns, setTimeSeriesColumns] = useState([]);
  const [decisionThreshold, setDecisionThreshold] = useState(0.5);
  const [amountColumn, setAmountColumn] = useState("signals.requestedAmount");
  const [postRules, setPostRules] = useState([]);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const next = await getPaymentPlatformMLWorkspace(tenantId);
      setWorkspace(next);
      const saved = next?.latestPipeline;
      if (saved?.config) {
        setCleaningRules(saved.config.cleaningRules || []);
        setTimeSeriesEnabled(saved.config.timeSeries?.enabled === true);
        setTimeSeriesColumns(saved.config.timeSeries?.columns || []);
        setDecisionThreshold(Number(saved.config.decisionThreshold ?? 0.5));
        setAmountColumn(saved.config.amountColumn || "signals.requestedAmount");
        setPostRules(saved.config.postRules || []);
        setResult(saved);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not load the MCA pipeline builder");
    } finally {
      setLoading(false);
    }
  }, [tenantId]);
  useEffect(() => { load(); }, [load]);

  const columns = useMemo(
    () => (workspace?.availableColumns || []).filter((column) => Number(column.coverage || 0) > 0),
    [workspace],
  );
  const timeCandidates = useMemo(
    () => columns.filter((column) => column.type === "number" && column.timeIndex != null),
    [columns],
  );
  const postColumns = useMemo(() => [
    { column: "model.probability", label: "Model probability", coverage: 100, type: "number" },
    { column: "model.forecastGrowth", label: "Forecast growth", coverage: 100, type: "number" },
    ...columns,
  ], [columns]);
  const amountColumns = useMemo(() => {
    const numericColumns = columns.filter((column) => column.type === "number");
    return numericColumns.some((column) => column.column === amountColumn)
      ? numericColumns
      : [{ column: amountColumn, label: amountColumn.replace(/^[^.]+\./, ""), type: "number", coverage: 0 }, ...numericColumns];
  }, [amountColumn, columns]);
  const addRule = (post = false) => {
    const source = post ? postColumns : columns;
    const next = emptyRule(source[0]?.column || "", post);
    if (post) setPostRules((current) => [...current, next]);
    else setCleaningRules((current) => [...current, next]);
  };
  const updateRule = (setter, index, changes) => setter((current) => current.map(
    (rule, ruleIndex) => ruleIndex === index ? { ...rule, ...changes } : rule,
  ));

  const run = async () => {
    setRunning(true);
    try {
      const next = await runPaymentPlatformModelPipeline(tenantId, {
        cleaningRules,
        timeSeries: { enabled: timeSeriesEnabled, columns: timeSeriesColumns },
        decisionThreshold,
        amountColumn,
        postRules,
      });
      setResult(next);
      toast.success("MCA model pipeline evaluated");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Pipeline evaluation failed");
    } finally {
      setRunning(false);
    }
  };

  if (loading && !workspace) {
    return <Box sx={{ py: 5, display: "grid", placeItems: "center" }}><CircularProgress aria-label="Loading MCA pipeline builder" /></Box>;
  }

  return (
    <Box data-admin-mca-pipeline>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <TuneRoundedIcon sx={{ color: C.blue }} />
            <Typography sx={{ color: C.ink, fontSize: 18, fontWeight: 950 }}>Executable model pipeline</Typography>
            <Chip size="small" label="Rules + time series + rules" color="primary" variant="outlined" />
          </Stack>
          <Typography sx={{ color: C.muted, fontSize: 12.5, mt: 0.4 }}>
            Clean the merchant population, train and evaluate a time-aware classifier, then apply final decision overrides.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={running ? <CircularProgress size={16} color="inherit" /> : <PlayArrowRoundedIcon />} disabled={running || (timeSeriesEnabled && timeSeriesColumns.length < 2)} onClick={run}>
          {running ? "Running…" : "Run pipeline"}
        </Button>
      </Stack>

      <Stack spacing={1.5}>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5, borderColor: C.line }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
            <Box>
              <Typography sx={{ color: C.ink, fontWeight: 900 }}>1. Clean the training data</Typography>
              <Typography sx={{ color: C.muted, fontSize: 12 }}>Excel-style filters are combined with AND; only matching merchants continue.</Typography>
            </Box>
            <Button size="small" startIcon={<AddRoundedIcon />} onClick={() => addRule(false)} disabled={!columns.length}>Add filter</Button>
          </Stack>
          {!cleaningRules.length ? <Alert severity="info">No cleaning filters: use the full portfolio.</Alert> : (
            <Stack spacing={1}>{cleaningRules.map((rule, index) => (
              <RuleRow
                key={`${index}-${rule.column}`}
                rule={rule}
                columns={columns}
                onChange={(changes) => updateRule(setCleaningRules, index, changes)}
                onDelete={() => setCleaningRules((current) => current.filter((_, ruleIndex) => ruleIndex !== index))}
              />
            ))}</Stack>
          )}
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5, borderColor: C.line }}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2}>
            <Box>
              <Typography sx={{ color: C.ink, fontWeight: 900 }}>2. Time-series model</Typography>
              <Typography sx={{ color: C.muted, fontSize: 12 }}>Use ordered monthly fields to learn level, slope, volatility, growth and next-period forecast.</Typography>
            </Box>
            <FormControlLabel control={<Switch checked={timeSeriesEnabled} onChange={(event) => setTimeSeriesEnabled(event.target.checked)} />} label={timeSeriesEnabled ? "Enabled" : "Disabled"} />
          </Stack>
          {timeSeriesEnabled && (
            <Box sx={{ mt: 1.5 }}>
              {!timeCandidates.length ? <Alert severity="warning">No ordered monthly numeric columns are available. Re-import them as retained merchant columns first.</Alert> : (
                <FormControl fullWidth size="small">
                  <InputLabel>Ordered monthly columns</InputLabel>
                  <Select
                    multiple
                    label="Ordered monthly columns"
                    value={timeSeriesColumns}
                    onChange={(event) => setTimeSeriesColumns(event.target.value)}
                    renderValue={(selected) => `${selected.length} columns selected`}
                  >
                    {timeCandidates.map((column) => (
                      <MenuItem key={column.column} value={column.column}>
                        <Checkbox checked={timeSeriesColumns.includes(column.column)} />
                        <ListItemText primary={column.label} secondary={`Period ${column.timeIndex} · ${column.coverage}% filled`} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          )}
          <Box sx={{ mt: 2, px: 1 }}>
            <Typography sx={{ color: C.muted, fontSize: 11.5 }}>Base acceptance threshold: {Math.round(decisionThreshold * 100)}%</Typography>
            <Slider value={decisionThreshold} min={0.1} max={0.9} step={0.05} onChange={(_, value) => setDecisionThreshold(value)} valueLabelDisplay="auto" valueLabelFormat={(value) => `${Math.round(value * 100)}%`} />
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5, borderColor: C.line }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
            <Box>
              <Typography sx={{ color: C.ink, fontWeight: 900 }}>3. Post-model decision rules</Typography>
              <Typography sx={{ color: C.muted, fontSize: 12 }}>Rules run in order; the first match overrides the model decision.</Typography>
            </Box>
            <Button size="small" startIcon={<AddRoundedIcon />} onClick={() => addRule(true)} disabled={!postColumns.length}>Add rule</Button>
          </Stack>
          {!postRules.length ? <Alert severity="info">No overrides: use the model threshold for every merchant.</Alert> : (
            <Stack spacing={1}>{postRules.map((rule, index) => (
              <RuleRow
                key={`${index}-${rule.column}`}
                rule={rule}
                columns={postColumns}
                post
                onChange={(changes) => updateRule(setPostRules, index, changes)}
                onDelete={() => setPostRules((current) => current.filter((_, ruleIndex) => ruleIndex !== index))}
              />
            ))}</Stack>
          )}
          <TextField select size="small" fullWidth label="Portfolio amount column" value={amountColumn} onChange={(event) => setAmountColumn(event.target.value)} sx={{ mt: 2 }}>
            {amountColumns.map((column) => <MenuItem key={column.column} value={column.column}>{column.label}</MenuItem>)}
          </TextField>
        </Paper>
      </Stack>

      {result && (
        <Box sx={{ mt: 2.5 }}>
          <Typography sx={{ color: C.ink, fontSize: 17, fontWeight: 950, mb: 1.25 }}>Evaluation results</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", lg: "repeat(4, 1fr)" }, gap: 1.25 }}>
            <Metric label="Precision" value={percent(result.metrics?.precision)} detail="Accepted merchants that performed" color={C.green} />
            <Metric label="Recall" value={percent(result.metrics?.recall)} detail="Performers captured by acceptance" />
            <Metric label="F2 score" value={percent(result.metrics?.f2)} detail="Recall weighted 4× vs precision" color={C.amber} />
            <Metric label="Holdout" value={Number(result.data?.validationRows || 0).toLocaleString()} detail={`${Number(result.data?.labelledRows || 0).toLocaleString()} labelled merchants`} />
            <Metric label="Accepted amount" value={money(result.portfolio?.acceptedAmount)} detail={`${Number(result.portfolio?.acceptedCount || 0).toLocaleString()} merchants · normalized full filtered portfolio`} color={C.green} />
            <Metric label="Rejected amount" value={money(result.portfolio?.rejectedAmount)} detail={`${Number(result.portfolio?.rejectedCount || 0).toLocaleString()} merchants · normalized full filtered portfolio`} color={C.red} />
            <Metric label="Filtered portfolio" value={Number(result.data?.cleanedRows || 0).toLocaleString()} detail={`${Number(result.data?.filteredOutRows || 0).toLocaleString()} removed by cleaning`} />
            <Metric label="Post-rule matches" value={Number(result.portfolio?.postRuleMatches || 0).toLocaleString()} detail={`${result.portfolio?.acceptanceRate ?? 0}% final acceptance rate`} color={C.amber} />
          </Box>
        </Box>
      )}
    </Box>
  );
}
